/* Pagina pubblica del Fascicolo Privacy v2 — accesso tramite link monouso.
 * URL: /fascicolo/:token
 * Layout: lista documenti su righe compatte (nome + file + KB + checkbox PV + bottoni azione).
 * Anteprima/Stampa/Download in modal popup a richiesta.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/fascicolo-link`;

export default function PublicFascicoloV2Page() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [marking, setMarking] = useState({});
  const [visione, setVisione] = useState({});
  const [previewModello, setPreviewModello] = useState(null);  // {modello_codice, modello_nome}

  const carica = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${BASE}/${encodeURIComponent(token)}`);
      setData(r.data);
      const v = {};
      (r.data?.documenti || []).forEach(d => {
        v[d.modello_codice] = d.presa_visione_at || null;
      });
      setVisione(v);
    } catch (e) {
      setErr(e.response?.data?.detail || "Link non valido o scaduto.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) carica(); /* eslint-disable-next-line */ }, [token]);

  const toggleVisione = async (modello_codice) => {
    const current = !!visione[modello_codice];
    const confermato = !current;
    setMarking(m => ({ ...m, [modello_codice]: true }));
    try {
      const r = await axios.post(
        `${BASE}/${encodeURIComponent(token)}/presa-visione/${encodeURIComponent(modello_codice)}`,
        { confermato },
      );
      setVisione(v => ({ ...v, [modello_codice]: r.data?.at || null }));
    } catch (e) {
      alert(e.response?.data?.detail || "Errore aggiornamento presa visione");
    } finally {
      setMarking(m => ({ ...m, [modello_codice]: false }));
    }
  };

  const pdfUrlOf = (mc) => `${BASE}/${encodeURIComponent(token)}/pdf/${encodeURIComponent(mc)}`;

  if (loading) {
    return <CenteredCard><div style={{ fontSize: 14, color: "#666" }}>⏳ Caricamento del fascicolo...</div></CenteredCard>;
  }
  if (err) {
    return (
      <CenteredCard>
        <div style={{ background: "#c62828", color: "white", padding: "14px 18px", fontWeight: 700, fontSize: 14, marginBottom: 14, borderRadius: 4 }} data-testid="public-fasc-error">
          ⚠ {err}
        </div>
        <div style={{ fontSize: 12, color: "#666" }}>
          Se il problema persiste contatta la segreteria.
        </div>
      </CenteredCard>
    );
  }

  const documenti = data?.documenti || [];
  const totDocs = documenti.length;
  const totVisti = documenti.filter(d => visione[d.modello_codice]).length;
  const ambitoLabel = data?.ambito === "trattamento" ? "Trattamento" : data?.ambito === "esterno" ? "Esterno" : "Docente";

  return (
    <div style={{ background: "#0a2342", minHeight: "100vh", paddingBottom: 60 }}>
      <style>{`@keyframes pulse-nuovo { 0%,100% { box-shadow: 0 0 0 0 rgba(239,108,0,0.6);} 50% { box-shadow: 0 0 0 6px rgba(239,108,0,0);} }`}</style>
      <header style={{ background: "linear-gradient(135deg, #0a2342, #1c3d6e)", padding: "24px 24px", color: "white", borderBottom: "3px solid #ef6c00" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: 0.5 }}>
            canossa<span style={{ color: "#ffd54f" }}>HUB</span>
          </div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>Fascicolo Privacy · Istituto Canossiano</div>
        </div>
      </header>

      <div style={{ maxWidth: 1280, margin: "20px auto", padding: "0 24px" }}>
        {/* Avviso rosso sulla composizione del fascicolo */}
        <div data-testid="fasc-warning-banner"
          style={{ background: "#ffebee", border: "1px solid #ef5350", color: "#b71c1c", padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 12, fontWeight: 700, textAlign: "center", letterSpacing: 0.3 }}>
          ⚠ Ogni modifica alla composizione del fascicolo o ai documenti in esso inseriti sarà oggetto di tempestiva comunicazione con eventuale richiesta di approvazione.
        </div>

        {/* Intestazione */}
        <div style={{ background: "white", padding: 18, borderRadius: 6, marginBottom: 16, boxShadow: "0 2px 12px rgba(0,0,0,0.15)" }}>
          <div style={{ fontSize: 11, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            Fascicolo {ambitoLabel}
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#0a2342" }} data-testid="public-fasc-label">{data?.label}</div>
          <div style={{ fontSize: 12, color: "#666", marginTop: 8, display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div>📄 <b>{totDocs}</b> document{totDocs === 1 ? "o" : "i"}</div>
            <div>✅ <b style={{ color: totVisti === totDocs && totDocs > 0 ? "#2e7d32" : "#ef6c00" }}>{totVisti}/{totDocs}</b> con presa visione</div>
            {data?.expires_at && <div>🗓 Scadenza link: <b>{new Date(data.expires_at).toLocaleDateString("it-IT")}</b></div>}
            {!data?.expires_at && <div style={{ color: "#888" }}>🔓 Link senza scadenza</div>}
          </div>
        </div>

        {totDocs === 0 && (
          <div style={{ background: "#fff8e1", border: "1px solid #ffe082", padding: 22, textAlign: "center", borderRadius: 4, color: "#6d4c00" }}>
            Nessun documento generato per questo fascicolo. Contatta la segreteria.
          </div>
        )}

        {/* Lista documenti compatta */}
        {totDocs > 0 && (
          <div style={{ background: "white", borderRadius: 6, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
            <div style={{ background: "#37474f", color: "white", padding: "10px 16px", fontSize: 13, fontWeight: 700 }}>
              📋 Documenti del fascicolo
            </div>
            <div>
              {documenti.map((d, i) => {
                const vis = !!visione[d.modello_codice];
                const isMarking = !!marking[d.modello_codice];
                const disabled = !d.ha_pdf;
                return (
                  <div key={d.modello_codice}
                    data-testid={`public-fasc-doc-${i}`}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(280px, 1fr) 240px minmax(220px, auto)",
                      gap: 14,
                      alignItems: "center",
                      padding: "12px 16px",
                      borderTop: i === 0 ? "none" : "1px solid #eee",
                      background: vis ? "#f1f8e9" : "white",
                      opacity: disabled ? 0.5 : 1,
                    }}>
                    {/* Colonna 1: Nome + filename + KB */}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#37474f", marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                        {d.nuovo && (
                          <span data-testid={`public-fasc-badge-nuovo-${i}`}
                            style={{ background: "#ef6c00", color: "white", padding: "2px 7px", borderRadius: 10, fontSize: 9, fontWeight: 800, letterSpacing: 0.5, animation: "pulse-nuovo 2s ease-in-out infinite" }}
                            title="Documento nuovo o aggiornato di recente">
                            🆕 NUOVO
                          </span>
                        )}
                        <span>{d.modello_nome || d.modello_codice}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "#888", fontFamily: "Consolas, monospace", display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.filename || d.modello_codice}</span>
                        {d.size && <span style={{ color: "#aaa", whiteSpace: "nowrap" }}>{Math.round(d.size / 1024)} KB</span>}
                      </div>
                    </div>

                    {/* Colonna 2: Checkbox presa visione */}
                    <div style={{ padding: "6px 10px", background: vis ? "#e8f5e9" : "#fafafa",
                      borderRadius: 4, border: `1px solid ${vis ? "#a5d6a7" : "#ddd"}` }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: disabled ? "not-allowed" : "pointer",
                        fontSize: 12, color: vis ? "#1b5e20" : "#555", fontWeight: 700 }}>
                        <input type="checkbox" checked={vis} disabled={disabled || isMarking}
                          onChange={() => toggleVisione(d.modello_codice)}
                          data-testid={`public-fasc-visione-${i}`}
                          style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span>{isMarking ? "⏳ Salvataggio..." : vis ? "✓ Presa visione confermata" : "Conferma presa visione"}</span>
                          {vis && visione[d.modello_codice] && (
                            <span style={{ fontSize: 10, color: "#666", fontWeight: 400 }}>
                              il {new Date(visione[d.modello_codice]).toLocaleString("it-IT")}
                            </span>
                          )}
                        </div>
                      </label>
                    </div>

                    {/* Colonna 3: Bottoni azione */}
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <button onClick={() => setPreviewModello(d)}
                        disabled={disabled}
                        data-testid={`public-fasc-anteprima-${i}`}
                        style={{ background: "#6a1b9a", color: "white", border: "none", padding: "6px 12px", fontSize: 12, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer", borderRadius: 3 }}>
                        👁 Anteprima
                      </button>
                      <a href={pdfUrlOf(d.modello_codice)} target="_blank" rel="noopener noreferrer"
                        data-testid={`public-fasc-download-${i}`}
                        style={{ background: "#1565c0", color: "white", textDecoration: "none", padding: "6px 12px", fontSize: 12, fontWeight: 700, borderRadius: 3, display: disabled ? "none" : "inline-block" }}>
                        ⬇ Scarica
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Riquadro Accettazione — sostituisce la vecchia sezione firma */}
        <div data-testid="accettazione-panel"
          style={{ marginTop: 24, padding: 20, background: "#ffcdd2", color: "#000",
            borderRadius: 4, border: "2px solid #ef9a9a",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)" }}>
          <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10, color: "#000" }}>
            ✍ Accettazione!
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6, color: "#000" }}>
            Si prega di Visionare e Accettare con l'apposizione della spunta ogni documento della lista
          </div>
          <div style={{ fontSize: 12, fontStyle: "italic", marginBottom: 12, color: "#000" }}>
            (comunicare eventuali errori ed omissioni)
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: "#000" }}>
            La spunta di presa visione e accettazione dei singoli documenti dell'elenco
          </div>
          <ol style={{ fontSize: 13, color: "#000", margin: "4px 0 0 22px", paddingLeft: 0, lineHeight: 1.6 }}>
            <li>fissa una marca temporale</li>
            <li>viene tracciata nel software</li>
            <li>vale come firma</li>
          </ol>
        </div>

        <div style={{ marginTop: 30, padding: 14, background: "rgba(255,255,255,0.06)", color: "#cfd8dc", fontSize: 11, textAlign: "center", borderRadius: 4 }}>
          Questo link è personale e tracciato. Le prese visione vengono registrate con data, ora e dispositivo.
          In caso di dubbi contatta la segreteria all'indirizzo segreteria@canossadiaz.it.
        </div>
      </div>

      {/* Modal Anteprima */}
      {previewModello && (
        <PreviewModal
          modello={previewModello}
          pdfUrl={pdfUrlOf(previewModello.modello_codice)}
          onClose={() => setPreviewModello(null)} />
      )}
    </div>
  );
}

function PreviewModal({ modello, pdfUrl, onClose }) {
  // Chiudi con ESC
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const stampa = () => {
    // Apre il PDF in una nuova finestra e lancia print
    const w = window.open(pdfUrl, "_blank");
    if (w) {
      w.addEventListener("load", () => { try { w.print(); } catch { /* noop */ } });
    }
  };

  return (
    <div onClick={onClose} data-testid="public-fasc-preview-modal"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 800,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: "white", width: "100%", maxWidth: 1100, height: "92vh",
          display: "flex", flexDirection: "column", borderRadius: 6, boxShadow: "0 12px 48px rgba(0,0,0,0.5)", overflow: "hidden" }}>
        <div style={{ background: "#37474f", color: "white", padding: "10px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>📄 {modello.modello_nome || modello.modello_codice}</div>
          <div style={{ fontSize: 10, opacity: 0.7, fontFamily: "Consolas, monospace" }}>{modello.filename || modello.modello_codice}</div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
            <button onClick={stampa} data-testid="public-fasc-preview-print"
              style={{ background: "#ef6c00", color: "white", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              🖨 Stampa
            </button>
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
              data-testid="public-fasc-preview-download"
              style={{ background: "#1565c0", color: "white", textDecoration: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, borderRadius: 3 }}>
              ⬇ Scarica
            </a>
            <button onClick={onClose} data-testid="public-fasc-preview-close"
              style={{ background: "rgba(255,255,255,0.15)", color: "white", border: "1px solid rgba(255,255,255,0.3)", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ✕ Chiudi
            </button>
          </div>
        </div>
        <iframe src={pdfUrl}
          title="Anteprima PDF"
          data-testid="public-fasc-preview-iframe"
          style={{ flex: 1, border: "none", background: "#525659", width: "100%" }} />
      </div>
    </div>
  );
}

function CenteredCard({ children }) {
  return (
    <div style={{ background: "#0a2342", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}>
      <div style={{ background: "white", padding: 30, borderRadius: 6, maxWidth: 480, boxShadow: "0 4px 20px rgba(0,0,0,0.3)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#0a2342", marginBottom: 14 }}>canossaHUB · Fascicolo Privacy</div>
        {children}
      </div>
    </div>
  );
}

/* ===================== FIRMA (dito o mouse) ===================== */
function FirmaPanel({ token, firma, setFirma }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [isEmpty, setIsEmpty] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);

  // Adatta canvas alla larghezza del container, con HiDPI scaling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || firma) return; // se già firmato, non disegniamo
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const w = Math.max(280, parent.clientWidth - 4);
      const h = 180;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = w * ratio;
      canvas.height = h * ratio;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, w, h);
      ctx.strokeStyle = "#0d47a1";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [firma]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
    setIsEmpty(false);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => { drawing.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const ratio = window.devicePixelRatio || 1;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    setIsEmpty(true);
    setErr(null);
  };

  const conferma = async () => {
    if (isEmpty || !canvasRef.current) {
      setErr("Per favore disegna la tua firma nel riquadro prima di confermare.");
      return;
    }
    setSaving(true); setErr(null); setSavedMsg(null);
    try {
      const data_url = canvasRef.current.toDataURL("image/png");
      const r = await axios.post(`${BASE}/${encodeURIComponent(token)}/firma`, { data_url });
      setFirma({ data_url, at: r.data?.at || new Date().toISOString() });
      setSavedMsg("Firma registrata correttamente.");
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore salvataggio firma.");
    } finally { setSaving(false); }
  };

  const rifirma = async () => {
    if (!window.confirm("Cancellare la firma registrata? Potrai disegnarne una nuova.")) return;
    try {
      await axios.delete(`${BASE}/${encodeURIComponent(token)}/firma`);
      setFirma(null); setIsEmpty(true); setSavedMsg(null); setErr(null);
    } catch (e) { setErr(e.response?.data?.detail || "Errore cancellazione firma."); }
  };

  return (
    <div data-testid="firma-panel"
      style={{ marginTop: 24, background: "white", borderRadius: 6, padding: 18, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#0a2342", marginBottom: 4 }}>
        ✍ Firma del destinatario
      </div>
      <div style={{ fontSize: 12, color: "#b71c1c", marginBottom: 6, fontWeight: 700, lineHeight: 1.4 }}>
        L'apposizione della firma sottintende la presa visione e accettazione dei documenti dell'elenco con spunta di presa visione.
      </div>
      <div style={{ fontSize: 12, color: "#555", marginBottom: 12 }}>
        Per concludere la presa visione, firma qui sotto con il dito (touch) o con il mouse.
      </div>
      {firma ? (
        <div data-testid="firma-saved" style={{ border: "2px solid #2e7d32", borderRadius: 4, padding: 12, background: "#f1f8e9" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1b5e20" }}>✓ Firma registrata</span>
            <span style={{ marginLeft: 8, fontSize: 11, color: "#555" }}>
              {firma.at ? `il ${new Date(firma.at).toLocaleString("it-IT")}` : ""}
            </span>
            <button onClick={rifirma} data-testid="firma-clear"
              style={{ marginLeft: "auto", padding: "4px 10px", background: "white", color: "#c62828", border: "1px solid #c62828", fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ✕ Cancella e rifirma
            </button>
          </div>
          {firma.data_url && (
            <img src={firma.data_url} alt="Firma"
              data-testid="firma-image"
              style={{ background: "white", maxWidth: "100%", border: "1px solid #e0e0e0", borderRadius: 3 }} />
          )}
        </div>
      ) : (
        <>
          <div style={{ border: "2px dashed #90caf9", borderRadius: 4, background: "#fafcff", padding: 2 }}>
            <canvas ref={canvasRef}
              data-testid="firma-canvas"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              style={{ display: "block", touchAction: "none", cursor: "crosshair", borderRadius: 3, background: "white" }} />
          </div>
          {err && <div style={{ marginTop: 8, padding: "6px 10px", background: "#ffebee", color: "#c62828", fontSize: 12, borderRadius: 3 }}>{err}</div>}
          {savedMsg && <div style={{ marginTop: 8, padding: "6px 10px", background: "#e8f5e9", color: "#1b5e20", fontSize: 12, borderRadius: 3 }}>{savedMsg}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button onClick={clearCanvas} disabled={saving} data-testid="firma-erase"
              style={{ padding: "8px 14px", background: "white", color: "#555", border: "1px solid #bbb", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 3 }}>
              ↺ Pulisci
            </button>
            <div style={{ flex: 1 }} />
            <button onClick={conferma} disabled={saving || isEmpty} data-testid="firma-submit"
              style={{ padding: "10px 22px", background: isEmpty || saving ? "#bdbdbd" : "#2e7d32", color: "white", border: "none", fontSize: 13, fontWeight: 700, cursor: saving || isEmpty ? "not-allowed" : "pointer", borderRadius: 3 }}>
              {saving ? "Salvataggio…" : "✓ Conferma firma"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
